import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { YouTubeVideo } from "@components/media/YouTubeVideo"
import { Chip } from "@components/ui/Chip"
import { HoverCard } from "@components/ui/HoverCard"
import { FilmSessionFragment, FilmTileFragment } from "@generated-types"
import { formatDateTime } from "@utils/date-format"
import { cn } from "@utils/tw"
import { ComponentProps, Suspense, useMemo } from "react"
import { EventBadges } from "./EventBadges"

type FilmTileProps = ContentProps &
  Omit<ComponentProps<typeof HoverCard>, "content"> & {
    placement?: "auto" | "center"
  }

export function FilmHoverCard(props: FilmTileProps) {
  const placement = props.placement ?? "auto"
  return (
    <HoverCard
      openDelay={300}
      closeDelay={50}
      content={<HoverContent film={props.film} session={props.session} />}
      arrow={false}
      className={
        // This is a hack and probably shouldn't stay as-is
        cn(
          placement === "center" && [
            "data-[side=bottom]:!translate-y-[calc(var(--radix-hover-card-trigger-height)/2_*_-1_-_50%)]",
            "data-[side=top]:!translate-y-[calc(var(--radix-hover-card-trigger-height)/2_+_50%)]",
            "md:w-[calc(var(--radix-hover-card-trigger-width)_*_1.3)] md:max-w-[--radix-hover-card-content-available-width]",
          ],
          placement === "auto" && "w-[400px]",
        )
      }
    >
      {props.children}
    </HoverCard>
  )
}

type ContentProps = {
  film: FilmTileFragment
  session?: FilmSessionFragment
}

function HoverContent(props: ContentProps) {
  const film = props.film
  const session = props.session

  const venue = props.session?.info?.venue?.titleSimple
  const venueSub = props.session?.info?.venue?.titleSub
  const date = props.session?.info?.dateTime

  const sessionTags = useMemo(() => {
    const tags: string[] = []
    if (date) {
      tags.push(formatDateTime(date))
    }
    if (venue) {
      tags.push(venueSub ? `${venue} ${venueSub}` : venue)
    }
    return tags
  }, [venue, date])

  const filmTypeLabel = film.eventInfo?.filmTypeLabel !== "Feature" ? film.eventInfo?.filmTypeLabel : null

  return (
    <div className={cn()}>
      {/* Image */}
      <div className="aspect-video bg-secondary">
        {!!props.film.eventInfo?.trailer ? (
          <Suspense fallback={<ResponsiveImage {...film.media?.thumb!} />}>
            <YouTubeVideo videoId={props.film.eventInfo?.trailer!} autoplay />
          </Suspense>
        ) : (
          <ResponsiveImage {...film.media?.thumb!} />
        )}
      </div>

      {/* Wishlist button */}
      {/* <div className="absolute top-1 right-1">
        <WishlistIcon film={session?.info?.ferveId!} />
      </div> */}

      {/* Info */}
      <div className="flex flex-col gap-3 p-3">
        {/* Film title/excerpt */}
        <div>
          <div className="type-label-m mb-2 text-fg">{film.title}</div>
          {film.excerpt && (
            <div
              className="type-body-s line-clamp-3 text-fg-secondary"
              dangerouslySetInnerHTML={{ __html: film.descriptions?.short! }}
            />
          )}
        </div>

        {/* Session info */}
        <div className="flex flex-wrap whitespace-nowrap text-ellipsis gap-2 type-caption">
          <EventBadges film={props.film} session={props.session} shape="rounded" />
          {filmTypeLabel && (
            <Chip variant="high" shape="rounded">
              {filmTypeLabel}
            </Chip>
          )}
          {sessionTags.map((tag, i) => (
            <Chip variant="high" shape="rounded" key={i}>
              {tag}
            </Chip>
          ))}
        </div>
      </div>
    </div>
  )
}
